/* eslint-disable max-lines */
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import { ITicketInvoiceOnlyCancellation } from "../../interfaces/reservations/Reservations.types";
import {
  invoiceCancelTicket,
  undoInvoiceCancelTicket
} from "../../services/OperatorInvoice.service";
import Button from "../ui/Button";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";
import useCan from "../users/permissions/useCan";

interface Props {
  ticketId: number;
  existing?: ITicketInvoiceOnlyCancellation;
}

type CurrentCancellation = {
  id: number;
  reason: string | null;
  consumed: boolean;
};

const resolveError = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: unknown } };
  if (typeof e?.response?.data === "string" && e.response.data) {
    return e.response.data;
  }
  return fallback;
};

const TicketInvoiceCancelAction: FC<Props> = ({ ticketId, existing }) => {
  const canDelete = useCan("accounting.invoicereport.delete");
  const canUpdate = useCan("accounting.invoicereport.update");
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState<CurrentCancellation | null>(
    existing
      ? { id: existing.id, reason: existing.reason, consumed: existing.consumed }
      : null
  );
  const [undoSubmitting, setUndoSubmitting] = useState(false);

  if (!canDelete && !current) return null;

  const reset = () => {
    setReason("");
    setShow(false);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error("Shkruaj arsyen e anulimit.", { position: "top-right" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await invoiceCancelTicket(ticketId, { reason: reason.trim() });
      setCurrent({
        id: res.data.cancellationId,
        reason: reason.trim(),
        consumed: false
      });
      toast.success("Bileta u shenua per anulim vetem ne fature.", {
        position: "top-right"
      });
      reset();
    } catch (err) {
      toast.error(
        resolveError(err, "Ndodhi nje gabim gjate anulimit."),
        { position: "top-right" }
      );
      setSubmitting(false);
    }
  };

  const handleUndo = async () => {
    if (!current || current.consumed) return;
    setUndoSubmitting(true);
    try {
      await undoInvoiceCancelTicket(current.id);
      toast.success("Anulimi u kthye mbrapa.", { position: "top-right" });
      setCurrent(null);
    } catch (err) {
      toast.error(
        resolveError(
          err,
          "Nuk mund te ben undo — anulimi mund te jete perfshire ne nje fature te gjeneruar."
        ),
        { position: "top-right" }
      );
    } finally {
      setUndoSubmitting(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      {current ? (
        <>
          <span
            className="text-xs text-orange-600 font-medium"
            title={current.reason || undefined}
          >
            {current.consumed
              ? `Anuluar ne fature · faturuar (#${current.id})`
              : `Anuluar ne fature (#${current.id})`}
          </span>
          {canUpdate && (
            <Button
              btnType="custom"
              padding="px-2 py-1"
              className="border border-gray-300 text-xs"
              onClick={handleUndo}
              disabled={undoSubmitting || current.consumed}
              title={
                current.consumed
                  ? "Anulimi eshte faturuar tashme — nuk mund te behet undo"
                  : undefined
              }
            >
              {undoSubmitting ? "..." : "Undo"}
            </Button>
          )}
        </>
      ) : (
        canDelete && (
          <Button
            btnType="custom"
            padding="px-2 py-1"
            className="border border-red-500 text-red-600 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              setShow(true);
            }}
          >
            Invoice-only cancel
          </Button>
        )
      )}

      <Modal
        show={show}
        position="center"
        onClose={() => setShow(false)}
        hasHeader={false}
        calcHeight={false}
      >
        <div className="flex justify-center mt-6 font-medium">
          Invoice-only cancel
          <IconBtn
            className="absolute right-2 top-2"
            onClick={() => setShow(false)}
          >
            <XMarkIcon className="w-4 h-4" strokeWidth={2} />
          </IconBtn>
        </div>
        <div className="px-4 mt-4 text-sm text-gray-600">
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md p-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 shrink-0" />
            <p className="text-xs">
              Kjo nuk e rimburson klientin. Vetem do te shfaqet si Anulim ne
              faturen e ardhshme te operatorit, nese bileta eshte perfshire ne
              nje fature te derguar.
            </p>
          </div>
          <label className="block text-xs font-medium mb-1">Arsyeja</label>
          <textarea
            className="w-full border rounded-md p-2 text-sm"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Shpjego pse po anulohet vetem ne fature..."
          />
        </div>
        <div className="flex gap-2 justify-end p-4">
          <Button
            btnType="custom"
            className="border border-gray-300"
            onClick={() => setShow(false)}
          >
            Anulo
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Duke kryer..." : "Konfirmo"}
          </Button>
        </div>
      </Modal>
    </span>
  );
};

export default TicketInvoiceCancelAction;
